/**
 * src/apiClient.js
 *
 * MSAL-aware transport over `authedFetch` (admin) and `fetch` (public booking
 * wizard). Every failure is normalized to an `ApiError` carrying a display-safe
 * message, a coarse `code`, a `correlationId`, and the HTTP status — see
 * apiError.js for the dependency-free core and the normalization contract.
 *
 * The backend already sanitizes error bodies to `{ error, code, correlationId }`
 * (api/shared/validate.js); this layer trusts `error` as display-safe and
 * supplies a generic fallback when a body is missing/unparseable or the network
 * is unreachable. Surface `err.message` (and optionally `err.correlationId`)
 * via a toast — never build a message from raw response text.
 */

import { authedFetch } from "./authFetch.js";
import { ApiError, normalize, runFetch } from "./apiError.js";

export { ApiError, normalize };

/**
 * Authenticated admin API call. Resolves to parsed JSON (or null); throws an
 * `ApiError` on any non-OK response or network failure.
 */
export function authedJson(instance, account, path, init = {}) {
  return runFetch(authedFetch(instance, account, path, init));
}

/**
 * Public (unauthenticated) API call — used by the booking wizard endpoints.
 * Same normalization/error contract as `authedJson`.
 */
export function publicJson(path, init = {}) {
  return runFetch(fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  }));
}
